// import type { IHttpError } from '@wix/http-client';
import type {
  Reporter,
  BaseParams,
  LogLevel,
  ErrorUserData,
  TransactionUserData,
  EventPayload,
  UserParams,
  PhaseUserData,
} from '@wix/panorama-common-shared/types';
import { TransactionAction } from '@wix/panorama-common-shared/types';
import type { GlobalConfig } from '@wix/panorama-common/global-config';
import {
  createDurationTracker,
  type DurationTracker,
} from '@wix/panorama-common/duration-tracker';
import {
  buildErrorPayload,
  buildLoggerPayload,
  buildPhasePayload,
  buildTransactionPayload,
} from './lib/payload-builder';
import { Hook, type HooksManager } from './plugins/hooks-manager';
import type {
  Breadcrumb,
  ErrorMonitorOptions,
  TransactionOptions,
} from './types';
import type { Scope } from './lib/scope';

interface Options {
  baseParams: BaseParams;
  globalConfig: GlobalConfig;
  reporter: Reporter;
  hooksManager: HooksManager;
  data: Scope<NonNullable<UserParams['data']>>;
  isMuted: () => boolean;
}

export class BasePanoramaClient {
  private durationTracker: DurationTracker;
  private readonly baseParams: BaseParams;
  private readonly globalConfig: GlobalConfig;
  private readonly reporter: Reporter;
  private readonly hooksManager: HooksManager;
  private readonly data: Scope<NonNullable<UserParams['data']>>;
  private readonly isMuted: () => boolean;

  constructor({
    baseParams,
    globalConfig,
    reporter,
    hooksManager,
    data,
    isMuted,
  }: Options) {
    this.baseParams = baseParams;
    this.globalConfig = globalConfig;
    this.reporter = reporter;
    this.hooksManager = hooksManager;
    this.data = data;
    this.isMuted = isMuted;

    // For backward compatibility in cases where GlobalConfig version is older than what we expect
    this.durationTracker =
      typeof globalConfig.getDurationTracker === 'function'
        ? globalConfig.getDurationTracker()
        : createDurationTracker();
  }

  private report(eventPayload: EventPayload) {
    if (this.isMuted()) {
      return;
    }

    const reporterOverride = this.globalConfig.getReporter();
    const beforeReportHookResult = this.hooksManager.invoke(
      Hook.beforeReport,
      eventPayload,
    );

    if (typeof reporterOverride === 'function') {
      reporterOverride(eventPayload);
      return;
    }

    if (!beforeReportHookResult) {
      return;
    }

    this.reporter(eventPayload);
  }

  getErrorMonitorDataWithDefaults = (
    ...args: (ErrorUserData | undefined)[]
  ) => {
    return this.data.addDataScope(args).getData();
  };

  reportTransactionStart(
    name: string,
    options: TransactionOptions = {},
    data?: TransactionUserData,
  ) {
    const { id } = options;
    const dataWithDefault = this.data.addDataScope(data).getData();

    if (
      !this.hooksManager.invoke(
        Hook.beforeReportTransactionStart,
        name,
        dataWithDefault,
      )
    ) {
      return;
    }

    const { fullArtifactId, componentId } = this.baseParams;

    this.durationTracker.markTransactionStart(
      fullArtifactId!,
      componentId!,
      name,
      id,
    );

    const payload = buildTransactionPayload(
      this.globalConfig,
      this.baseParams,
      {
        transactionName: name,
        transactionAction: TransactionAction.Start,
      },
      dataWithDefault,
    );

    this.report(payload);

    this.hooksManager.invoke(Hook.afterReportTransactionStart, name, payload);
  }

  reportTransactionFinish(
    name: string,
    options: TransactionOptions = {},
    data?: TransactionUserData,
  ) {
    const { id } = options;
    const dataWithDefault = this.data.addDataScope(data).getData();

    if (
      !this.hooksManager.invoke(
        Hook.beforeReportTransactionFinish,
        name,
        dataWithDefault,
      )
    ) {
      return;
    }

    const { fullArtifactId, componentId } = this.baseParams;

    const duration = this.durationTracker.markTransactionFinish(
      fullArtifactId!,
      componentId!,
      name,
      id,
    );

    const payload = buildTransactionPayload(
      this.globalConfig,
      this.baseParams,
      {
        transactionName: name,
        transactionAction: TransactionAction.Finish,
        transactionDuration: duration,
      },
      dataWithDefault,
    );

    this.report(payload);

    this.hooksManager.invoke(Hook.afterReportTransactionFinish, name, payload);
  }

  reportPhaseStart(name: string, data?: PhaseUserData) {
    const dataWithDefault = this.data.addDataScope(data).getData();

    if (
      !this.hooksManager.invoke(
        Hook.beforeReportPhaseStart,
        name,
        dataWithDefault,
      )
    ) {
      return;
    }

    const { fullArtifactId, componentId } = this.baseParams;

    this.durationTracker.markPhaseStart(fullArtifactId!, componentId!, name);

    const payload = buildPhasePayload(
      this.globalConfig,
      this.baseParams,
      {
        phaseName: name,
        transactionAction: TransactionAction.Start,
      },
      dataWithDefault,
    );

    this.report(payload);

    this.hooksManager.invoke(Hook.afterReportPhaseStart, name, payload);
  }

  reportPhaseFinish(name: string, data?: TransactionUserData) {
    const dataWithDefault = this.data.addDataScope(data).getData();

    if (
      !this.hooksManager.invoke(
        Hook.beforeReportPhaseFinish,
        name,
        dataWithDefault,
      )
    ) {
      return;
    }

    const { fullArtifactId, componentId } = this.baseParams;

    const duration = this.durationTracker.markPhaseFinish(
      fullArtifactId!,
      componentId!,
      name,
    );

    const payload = buildPhasePayload(
      this.globalConfig,
      this.baseParams,
      {
        phaseName: name,
        transactionAction: TransactionAction.Finish,
        transactionDuration: duration,
      },
      dataWithDefault,
    );

    this.report(payload);

    this.hooksManager.invoke(Hook.afterReportPhaseFinish, name, payload);
  }

  reportError(
    error: Error,
    data?: ErrorUserData,
    options?: ErrorMonitorOptions,
  ) {
    if (!(error instanceof Error)) {
      return;
    }

    data = this.data.addDataScope([data, options?.data]).getData();

    if (
      !this.hooksManager.invoke(Hook.beforeReportError, error, data, options)
    ) {
      return;
    }

    const payload = buildErrorPayload(
      this.globalConfig,
      this.baseParams,
      error,
      data,
    );

    this.report(payload);

    this.hooksManager.invoke(Hook.afterReportError, error, data, options);
  }

  addBreadcrumb(breadcrumb: Breadcrumb) {
    if (!this.hooksManager.invoke(Hook.beforeAddBreadcrumb, breadcrumb)) {
      return;
    }

    // Currently the default implementation does nothing, we only invoke hooks

    this.hooksManager.invoke(Hook.afterAddBreadcrumb, breadcrumb);
  }

  log(logLevel: LogLevel, message: string, data?: Record<string, any>) {
    if (!message) {
      return;
    }

    const dataWithDefault = this.data.addDataScope(data).getData();

    const loggerParams = {
      message,
      data: dataWithDefault,
      logLevel,
    };

    if (
      !this.hooksManager.invoke(
        Hook.beforeReportLog,
        logLevel,
        message,
        dataWithDefault,
      )
    ) {
      return;
    }

    const payload = buildLoggerPayload(
      this.globalConfig,
      this.baseParams,
      loggerParams,
    );
    this.report(payload);

    this.hooksManager.invoke(
      Hook.afterReportLog,
      logLevel,
      message,
      dataWithDefault,
    );
  }
}
